/**
 * Shared wizard primitives — canvas patterns 1/3/5/6/7 from wizard-design-spec.md.
 * Styling: Core NAMED tokens only (coral = azure primary slot, teal = neutral grey slot).
 * Status colors (verification state) use the status-* CSS vars per the repo trend/status rule.
 */
import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { BibleScore } from '@/features/brand-bible-wizard/types';
import { BRAND_BIBLE_SCORE_MAX, LAUNCH_THRESHOLD } from '@/features/brand-bible-wizard/types';

/* ---------------------------------- ScoreChip --------------------------------- */

export function ScoreChip({ score, delta }: { score: BibleScore | null; delta: number | null }) {
  const value = score?.score ?? null;
  const ok = value !== null && value >= LAUNCH_THRESHOLD;
  const circ = 2 * Math.PI * 14;
  const frac = value !== null ? Math.min(1, value / BRAND_BIBLE_SCORE_MAX) : 0;
  return (
    <div className="flex items-center gap-4">
      {delta !== null && delta !== 0 && (
        <div className="text-right text-xs text-muted-foreground">
          All changes saved
          <br />
          <span className="font-semibold text-coral tabular-nums">+{delta} pts this session</span>
        </div>
      )}
      <div className="flex items-center gap-2.5 rounded-lg border border-border bg-warm-bg py-1.5 pl-2 pr-3">
        <svg width="34" height="34" viewBox="0 0 34 34" aria-hidden>
          <circle cx="17" cy="17" r="14" fill="none" strokeWidth="4" className="stroke-teal-light" />
          <circle
            cx="17"
            cy="17"
            r="14"
            fill="none"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={`${circ * frac} ${circ * (1 - frac)}`}
            transform="rotate(-90 17 17)"
            stroke={ok ? 'hsl(var(--status-success))' : 'hsl(var(--status-warning))'}
          />
        </svg>
        <div>
          <div className="leading-none">
            <span
              className="text-2xl font-bold tabular-nums"
              style={{ color: ok ? 'hsl(var(--status-success))' : 'hsl(var(--status-warning))' }}
            >
              {value ?? '—'}
            </span>
            <span className="ml-0.5 text-xs font-medium text-muted-foreground">/{BRAND_BIBLE_SCORE_MAX}</span>
          </div>
          <div className="text-[11px] font-medium text-muted-foreground">Launch at {LAUNCH_THRESHOLD}</div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------- EnvelopeBadge -------------------------------- */

export type EnvelopeBadgeKind = 'discovered' | 'verified' | 'public' | 'source' | 'low' | 'auto' | 'rejected';

const BADGE_STYLES: Record<EnvelopeBadgeKind, string> = {
  discovered: 'bg-[hsl(var(--status-warning-bg))] text-[hsl(var(--status-warning))] border border-[hsl(var(--status-warning)/0.3)]',
  verified: 'bg-[hsl(var(--status-success-bg))] text-[hsl(var(--status-success))]',
  public: 'bg-coral-light text-coral-dark',
  source: 'bg-teal-light text-teal',
  low: 'bg-[hsl(var(--status-warning-bg))] text-[hsl(var(--status-warning))]',
  auto: 'bg-coral-light text-coral-dark border border-coral/25',
  rejected: 'bg-destructive/10 text-destructive',
};

export function EnvelopeBadge({ kind, children }: { kind: EnvelopeBadgeKind; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'rounded-[5px] px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide',
        BADGE_STYLES[kind],
      )}
    >
      {children}
    </span>
  );
}

/* ------------------------------- VerificationRow ------------------------------- */

export function VerificationRow({
  verifiedBy,
  verifiedAt,
  onVerify,
  canVerify,
}: {
  verifiedBy: string | null;
  verifiedAt?: string | null;
  onVerify?: () => void;
  /** false for DIY / non-editor viewers — hides the affordance entirely (contract §4.3). */
  canVerify: boolean;
}) {
  if (verifiedBy) {
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--status-success))]">
        <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--status-success))]" />
        Verified{verifiedAt ? ` · ${new Date(verifiedAt).toLocaleDateString()}` : ''}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 text-[11px] text-[hsl(var(--status-warning))]">
      <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--status-warning))]" />
      Pending verification
      {canVerify && onVerify && (
        <button type="button" onClick={onVerify} className="font-semibold text-coral hover:text-coral-hover">
          Verify
        </button>
      )}
    </div>
  );
}

/* ------------------------------------ Field ------------------------------------ */

export function Field({
  label,
  required,
  hint,
  children,
  verification,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
  verification?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline gap-1.5">
        <label className="text-xs font-semibold text-teal-dark">
          {label}
          {required && <span className="ml-0.5 text-coral">*</span>}
        </label>
        {hint && <span className="ml-auto text-[11px] text-muted-foreground">{hint}</span>}
      </div>
      {children}
      {verification}
    </div>
  );
}

export const inputClass =
  'w-full rounded-lg border border-input bg-warm-bg px-3 py-2 text-sm text-foreground ' +
  'outline-none focus:border-coral focus:ring-2 focus:ring-ring/40';

/* -------------------------------- ExplicitNone --------------------------------- */

/**
 * "None" is an answer and scores; blank doesn't (contract §6). Records a real
 * explicit-none entry via the provided handler.
 */
export function ExplicitNoneButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rounded-lg border border-dashed border-input bg-warm-bg px-3.5 py-2 text-xs font-medium text-teal hover:border-coral hover:text-coral-dark disabled:opacity-50"
    >
      {label}
    </button>
  );
}

/* -------------------------------- SectionHeader -------------------------------- */

export function SectionHeader({
  tile,
  kicker,
  title,
  pointsNote,
}: {
  tile: string;
  kicker: string;
  title: string;
  pointsNote: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="grid h-[34px] w-[34px] flex-shrink-0 place-items-center rounded-lg border border-border bg-warm-bg font-mono text-[13px] font-semibold text-coral">
        {tile}
      </div>
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{kicker}</div>
        <div className="mt-0.5 text-xl font-semibold -tracking-[0.01em]">{title}</div>
      </div>
      <div className="ml-auto rounded-md bg-teal-light px-2 py-1 font-mono text-[11px] text-teal">{pointsNote}</div>
    </div>
  );
}

/* ---------------------------------- StepCard ----------------------------------- */

export function StepCard({
  title,
  headerRight,
  children,
  className,
}: {
  title: string;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('rounded-[10px] border border-border bg-warm-bg', className)}>
      <div className="flex items-center gap-2 border-b border-border/60 px-4 py-3">
        <div className="text-[13px] font-semibold">{title}</div>
        {headerRight && <div className="ml-auto flex items-center gap-2">{headerRight}</div>}
      </div>
      {children}
    </div>
  );
}

/* --------------------------------- WizardFooter -------------------------------- */

export function WizardFooter({
  backLabel,
  nextLabel,
  onBack,
  onNext,
  saving,
  deltaNote,
}: {
  backLabel: string;
  nextLabel: string;
  onBack: () => void;
  onNext: () => void;
  saving: boolean;
  deltaNote?: string;
}) {
  return (
    <div className="mt-auto flex items-center gap-3.5 border-t border-border pt-3.5">
      <button
        type="button"
        onClick={onBack}
        className="rounded-lg border border-input bg-warm-bg px-3.5 py-2 text-xs font-semibold text-teal-dark hover:bg-teal-light"
      >
        {backLabel}
      </button>
      <span className="text-xs text-muted-foreground">
        {saving ? 'Saving…' : 'Autosaved'}
        {deltaNote && (
          <>
            {' · '}
            <span className="font-semibold text-[hsl(var(--status-success))]">{deltaNote}</span>
          </>
        )}
      </span>
      <button
        type="button"
        onClick={onNext}
        className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-coral px-3.5 py-2 text-xs font-semibold text-white hover:bg-coral-hover"
      >
        {nextLabel}
      </button>
    </div>
  );
}

/* --------------------------------- misc helpers -------------------------------- */

export function VerifiedCheck() {
  return <Check className="size-3.5 text-[hsl(var(--status-success))]" aria-hidden />;
}
